import { Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import axios from "axios";
import styles from "./SignupForm.module.css";
import { useState, forwardRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { Link } from "react-router-dom";
import { isValidPhoneNumber } from "react-phone-number-input";
import PhoneInput from "react-phone-number-input";
import { usePlacesWidget } from "react-google-autocomplete";
import { CountryList } from "../../../admin/components/Constants";

const CustomInput = forwardRef((props, ref) => (<TextField {...props} inputRef={ref} id="outlined-basic" label="Phone*" variant="outlined" />));

export default function SignupForm({ onSubmit, setLoading }) {
  const [errorMsg, setErrorMsg] = useState("");

  const { register, handleSubmit, setValue, control, watch, formState: { errors } } = useForm({
    mode: "onChange",
  });

  const { ref } = usePlacesWidget({
    apiKey: process.env.REACT_APP_MAP_API_KEY,
    onPlaceSelected: (place) => {
      setValue("company_address", place.formatted_address);
    },
    options: {
      types: ["address"],
      componentRestrictions: { country: 'GB' },
    },
  });

  async function onSubmitLocal(data) {
    setLoading(true);
    setErrorMsg("");

    return axios.post(process.env.REACT_APP_API_URL + "v1/signup", data).then((response) => {
      setLoading(false);
      if (response.data.status === "SUCCESS") {
        onSubmit(response.data.data, response.data.userData);
      }
      if (response.data.status === "ERROR") {
        setErrorMsg(response?.data?.message);
      }
    }).catch((error) => {
      if (error && error?.response && error?.response?.data && error?.response?.data?.message) {
        setErrorMsg(error?.response?.data?.message);
      }
      setLoading(false);
    });
  }

  return (<>
    <form onSubmit={handleSubmit(onSubmitLocal)}>
      <p className={`${styles.ErrorM2}`}>{errorMsg}</p>
      <div className={`${styles.FormRow}`}>
        <div className={`${styles.FormGroupFull} FormGroup`}>
          <Controller
            control={control}
            {...register("company_name", { required: "This field is Required" })}
            render={({ field }) => (<TextField {...field} label="Company Name*" variant="outlined" />)}
          />
          {errors.company_name && (<span className={`${styles.ErrorM}`}>{errors?.company_name?.message}</span>)}
        </div>
        <div className={`${styles.FormGroupHalf} FormGroup`}>
          <Controller
            control={control}
            {...register("company_address", { required: "This field is Required" })}
            render={({ field }) => (<TextField {...field} label="Company Address*" variant="outlined" inputRef={ref} placeholder="" />)}
          />
          {errors.name && (<span className={`${styles.ErrorM}`}>{errors?.company_address?.message}</span>)}
        </div>
        <div className={`${styles.FormGroupHalf} FormGroup`}>
          <Controller
            control={control}
            {...register("name", { required: "This field is Required" })}
            render={({ field }) => (<TextField {...field} label="Owner Name*" variant="outlined" />)}
          />
          {errors.name && (<span className={`${styles.ErrorM}`}>{errors?.name?.message}</span>)}
        </div>
        <div className={`${styles.FormGroupHalf} FormGroup`}>
          <Controller
            control={control}
            {...register("email", {
              required: "This field is Required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Invalid email address",
              },
            })}
            render={({ field }) => (<TextField {...field} label="Email*" variant="outlined" />)}
          />
          {errors.email && (<span className={`${styles.ErrorM}`}>{errors.email.message}</span>)}
        </div>
        <div className={`${styles.FormGroupHalf} FormGroup`}>
          <Controller
            control={control}
            {...register("phone", {
              required: "This field is Required",
              validate: isValidPhoneNumber,
            })}
            render={({ field }) => (<PhoneInput {...field} defaultCountry="GB" inputComponent={CustomInput} />)}
          />
          {errors.phone && errors.phone.type !== "validate" && (<span className={`${styles.ErrorM}`}>{errors.phone.message}</span>)}
          {errors.phone && errors.phone.type === "validate" && (<span className={`${styles.ErrorM}`}>Please enter valid phone</span>)}
        </div>
        <div className={`${styles.FormGroupHalf} FormGroup`}>
          <Controller
            {...register("password", { required: "This field is Required" })}
            control={control}
            render={({ field }) => (<TextField {...field} label="Password*" variant="outlined" type="password" />)}
          />
          {errors.password && (<span className={`${styles.ErrorM}`}>{errors.password.message}</span>)}
        </div>
        <div className={`${styles.FormGroupHalf} FormGroup`}>
          <Controller
            {...register("conf_password", {
              required: "This field is Required",
              validate: (val) => {
                if (watch("password") !== val) {
                  return "Your passwords do no match";
                }
              },
            })}
            control={control}
            render={({ field }) => (<TextField {...field} id="outlined-basic" label="Confirm Password*" variant="outlined" type="password" />)}
          />
          {errors.conf_password && (<span className={`${styles.ErrorM}`}>{errors?.conf_password?.message}</span>)}
        </div>
        <div className={`${styles.FormGroupHalf} FormGroup`}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Pickup Country*</InputLabel>
            <Controller
              control={control}
              {...register("pickup_country", { required: "This field is required" })}
              render={({ field }) => (<Select label="Pickup Country*" {...field}>
                <MenuItem value={"GB"}>United Kingdom</MenuItem>
              </Select>)}
            />
          </FormControl>
          {errors.pickup_country && (<span className={`${styles.ErrorM}`}>{errors?.pickup_country?.message}</span>)}
        </div>
        <div className={`${styles.FormGroupHalf} FormGroup`}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Delivery Country*</InputLabel>
            <Controller
              control={control}
              {...register("delivery_country", { required: "This field is required" })}
              render={({ field }) => (<Select label="Delivery Country*" {...field}>
                {CountryList?.map((i) => {
                  return (<MenuItem key={i?.value} value={i?.value}>{i?.label}</MenuItem>);
                })}
              </Select>)}
            />
          </FormControl>
          {errors.delivery_country && (<span className={`${styles.ErrorM}`}>{errors?.delivery_country?.message}</span>)}
        </div>
        <div>
          <FormControl fullWidth>
            <FormControlLabel
              control={
                <Checkbox
                  {...register('terms', { required: 'You must accept the terms and conditions' })}
                  error={!!errors.terms}
                />
              }
              label={<>
                I agree to the{' '}
                <Link to="/terms" target="_blank" rel="noopener noreferrer" className={`${styles.ForhotPassText}`}>
                  Terms and Conditions
                </Link>
              </>
              }
            />
          </FormControl>
          {errors.terms && (<span className={`${styles.ErrorM}`}>{errors?.terms?.message}</span>)}
        </div>
      </div>

      <button type="submit" className={`${styles.LoginBU}`}>
        <span>Register</span>
      </button>

      <p className={`${styles.LoginFormSText}`}>Already have an account? <Link to={"/sign-in"} className={`${styles.ForhotPassText}`}>Login</Link></p>
    </form>
  </>);
}